/**
 * TanStack Query hooks for `advertiser-macros`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	remove,
	render,
	update,
	advertiserMacrosQueryKeys,
	type AdvertiserMacroRow,
	type CreateAdvertiserMacroBody,
	type RenderResult,
	type UpdateAdvertiserMacroBody,
} from "./api";

export function useAdvertiserMacrosList(
	advertiserId: string,
): UseQueryResult<AdvertiserMacroRow[], Error> {
	return useQuery({
		queryKey: advertiserMacrosQueryKeys.list(advertiserId),
		queryFn: () => list(advertiserId),
		enabled: advertiserId.trim().length > 0,
	});
}

export function useAdvertiserMacrosCreate(): UseMutationResult<
	AdvertiserMacroRow,
	Error,
	CreateAdvertiserMacroBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserMacrosQueryKeys.all });
		},
	});
}

export function useAdvertiserMacrosUpdate(): UseMutationResult<
	AdvertiserMacroRow,
	Error,
	{ id: string; body: UpdateAdvertiserMacroBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => update(id, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserMacrosQueryKeys.all });
		},
	});
}

export function useAdvertiserMacrosDelete(): UseMutationResult<
	{ id: string; deleted: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: advertiserMacrosQueryKeys.all });
		},
	});
}

export function useAdvertiserMacrosRender(): UseMutationResult<
	RenderResult,
	Error,
	{ id: string; context: Record<string, unknown> }
> {
	return useMutation({
		mutationFn: ({ id, context }) => render(id, context),
	});
}
